import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { formatDetail } from "@/utils/formatDetail";
import { Loader2, Eye, Trash2, X, FileText, Printer, Mail, Copy, FileDown } from "lucide-react";
import { QuotationSheet } from "@/components/QuotationSheet";
import { downloadPdf } from "@/utils/downloadPdf";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};
const STATUS_STYLE = {
  CONFIRMED: "bg-green-600/30 text-green-300",
  DRAFT: "bg-yellow-600/30 text-yellow-300",
  CANCELLED: "bg-red-600/30 text-red-300",
};

export const AdminQuotations = ({ client, api, perms = [] }) => {
  const canEdit = perms.includes("quotations:edit");
  const canDelete = perms.includes("quotations:delete");
  const canEmail = perms.includes("quotations:email");
  const canRevise = perms.includes("quotations:revise");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`${api}/qv2/quotations`);
      setQuotes(data);
    } catch {
      toast.error("Could not load quotations");
    } finally {
      setLoading(false);
    }
  }, [client, api]);

  useEffect(() => {
    load();
  }, [load]);

  const openView = async (id) => {
    try {
      const { data } = await client.get(`${api}/qv2/quotations/${id}`);
      setView(data);
    } catch {
      toast.error("Could not open quotation");
    }
  };

  const setStatus = async (q, status) => {
    try {
      await client.patch(`${api}/qv2/quotations/${q.id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete quotation ${q.quote_no}?`)) return;
    try {
      await client.delete(`${api}/qv2/quotations/${q.id}`);
      toast.success("Quotation removed");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const sendEmail = async () => {
    setEmailBusy(true);
    try {
      const { data } = await client.post(`${api}/qv2/quotations/${view.id}/email`);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail, "Could not send email"));
    } finally {
      setEmailBusy(false);
    }
  };

  const revise = async (q) => {
    try {
      const { data } = await client.post(`${api}/qv2/quotations/${q.id}/revise`);
      toast.success(`Revision ${data.quote_no} created as DRAFT`);
      load();
    } catch {
      toast.error("Could not revise quotation");
    }
  };

  return (
    <div data-testid="quotations-panel">
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-4">
        Archive ({quotes.length}) {loading && <Loader2 size={12} className="inline animate-spin ml-2" />}
      </p>

      {quotes.length === 0 && !loading ? (
        <div data-testid="quotations-empty" className="bg-steel border border-white/10 p-14 text-center">
          <FileText size={40} className="mx-auto text-smoke" strokeWidth={1.5} />
          <p className="mt-4 font-mono text-xs tracking-[0.3em] uppercase text-smoke">No quotations yet</p>
        </div>
      ) : (
        <div className="border border-white/10 bg-steel overflow-x-auto">
          <table data-testid="quotations-table" className="w-full min-w-[860px]">
            <thead>
              <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                <th className="py-3 px-4 font-medium">Quote #</th>
                <th className="py-3 px-4 font-medium">Date</th>
                <th className="py-3 px-4 font-medium">Customer</th>
                <th className="py-3 px-4 font-medium">Items</th>
                <th className="py-3 px-4 font-medium">Total Wt</th>
                <th className="py-3 px-4 font-medium">Grand Total</th>
                <th className="py-3 px-4 font-medium">Status</th>
                <th className="py-3 px-4 font-medium">By</th>
                <th className="py-3 px-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} data-testid={`quote-row-${q.quote_no}`} className="border-t border-white/10 hover:bg-ink/40 transition-colors duration-300">
                  <td className="py-3.5 px-4 font-mono text-xs text-[#7FB3E8] whitespace-nowrap">{q.quote_no}</td>
                  <td className="py-3.5 px-4 font-mono text-xs text-smoke whitespace-nowrap">{fmtDate(q.created_at)}</td>
                  <td className="py-3.5 px-4 font-heading font-semibold text-base uppercase tracking-wide text-white whitespace-nowrap">{q.customer?.company}</td>
                  <td className="py-3.5 px-4 font-mono text-xs text-smoke">{q.items?.length}</td>
                  <td className="py-3.5 px-4 font-mono text-xs text-smoke">{q.total_kg}</td>
                  <td className="py-3.5 px-4 font-mono text-xs text-white whitespace-nowrap">{fmt(q.grand_total)}</td>
                  <td className="py-3.5 px-4">
                    <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-1 ${STATUS_STYLE[q.status] || ""}`}>{q.status}</span>
                  </td>
                  <td className="py-3.5 px-4 font-mono text-xs text-smoke">{q.created_by}</td>
                  <td className="py-3.5 px-4 text-right whitespace-nowrap">
                    <button data-testid={`quote-view-${q.quote_no}`} onClick={() => openView(q.id)} className="font-mono text-[10px] tracking-widest uppercase text-[#7FB3E8] hover:text-white transition-colors duration-300 px-2 py-1 flex items-center gap-1 inline-flex">
                      <Eye size={13} /> View
                    </button>
                    {canRevise && (
                      <button data-testid={`quote-revise-${q.quote_no}`} onClick={() => revise(q)} className="font-mono text-[10px] tracking-widest uppercase text-[#7FB3E8] hover:text-white transition-colors duration-300 px-2 py-1 inline-flex items-center gap-1">
                        <Copy size={13} /> Revise
                      </button>
                    )}
                    {canEdit && (
                      <select
                        data-testid={`quote-status-${q.quote_no}`}
                        value={q.status}
                        onChange={(e) => setStatus(q, e.target.value)}
                        className="ml-2 bg-ink border border-white/20 font-mono text-[10px] uppercase text-white/70 px-1.5 py-1 focus:outline-none"
                      >
                        <option>CONFIRMED</option>
                        <option>DRAFT</option>
                        <option>CANCELLED</option>
                      </select>
                    )}
                    {canDelete && (
                      <button data-testid={`quote-delete-${q.quote_no}`} onClick={() => remove(q)} aria-label="Delete" className="p-2 text-white/50 hover:text-red-400 transition-colors duration-300">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view && (
        <div className="fixed inset-0 z-50 bg-ink/90 overflow-y-auto p-4 md:p-10" data-testid="quote-view-modal">
          <div className="quote-sheet max-w-3xl mx-auto bg-white text-ink p-8 md:p-12 relative">
            <button data-testid="quote-view-close" onClick={() => setView(null)} aria-label="Close" className="no-print absolute top-4 right-4 p-2 text-ink/50 hover:text-ink">
              <X size={22} />
            </button>

            <div className="no-print flex flex-wrap items-center gap-2 mb-6">
              <button data-testid="quote-print-button" onClick={() => window.print()} className="bg-royal text-white font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 flex items-center gap-2 hover:bg-ink transition-colors duration-300">
                <Printer size={15} /> Print / Save PDF
              </button>
              <button data-testid="quote-pdf-button" onClick={() => downloadPdf(client, `${api}/qv2/quotations/${view.id}/pdf`, `${view.quote_no.replace(/\//g, "-")}.pdf`)} className="border border-royal text-royal font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 flex items-center gap-2 hover:bg-royal hover:text-white transition-colors duration-300">
                <FileDown size={15} /> Download PDF
              </button>
              {canEmail && (
                <button data-testid="quote-email-button" onClick={sendEmail} disabled={emailBusy} className="border border-royal text-royal font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 flex items-center gap-2 hover:bg-royal hover:text-white transition-colors duration-300 disabled:opacity-60">
                  {emailBusy ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Email to Customer
                </button>
              )}
            </div>

            <QuotationSheet quote={view} />
          </div>
        </div>
      )}
    </div>
  );
};
